import React from "react";
import { Text, View } from "../../../platformComponents";
import { userCountriesData } from "@aspen/assets/offlineData/usersCountries";
import { styles } from "./EditableCommonStyles";
import { colors } from "../../../styles/common";
import dayjs from "dayjs";

// 文本截断工具函数
export const truncateText = (text: string, maxLength = 50) => {
  if (!text) return "";
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
};

// 获取国籍显示内容
export const getNationalityDisplay = (nationalityId: string | string[]) => {
  if (!nationalityId) return null;

  let countries;
  if (Array.isArray(nationalityId)) {
    countries = userCountriesData.filter(option => nationalityId.includes(option.id));
  } else {
    const country = userCountriesData.find(option => option.id === nationalityId);
    countries = country ? [country] : [];
  }

  if (countries.length === 0) {
    return <Text style={styles.fieldValueText}>{nationalityId}</Text>;
  }

  return (
    <View style={styles.nationalityContainer}>
      {countries.map((country, index) => (
        <View
          key={country.id}
          style={{
            flexDirection: "row",
            alignItems: "center",
            textWrap: "nowrap",
            overflow: "hidden",
          }}>
          {/*{country.flagIsImage ? (*/}
          {/*  <CommonImage*/}
          {/*    source={twIcon}*/}
          {/*    style={styles.nationalityFlag}*/}
          {/*    resizeMode="cover"*/}
          {/*  />*/}
          {/*) : (*/}
          {/*  <Text style={styles.nationalityFlag}>{country.nationalFlag}</Text>*/}
          {/*)}*/}
          <Text style={styles.nationalityFlag}>{country.nationalFlag}</Text>
          <Text style={styles.nationalityText}>{`${country.nationality}(${country.id})`}</Text>
          {index < countries.length - 1 && <Text style={{ color: colors.white }}> / </Text>}
          {/* 添加分隔符 */}
        </View>
      ))}
    </View>
  );
};

export const createRenderUtils = () => {
  const createTextRenderer = (maxLength?: number, defaultValue?: string) => {
    return (value: any) => {
      const _value = value?.toString() || defaultValue;
      if (!_value) return null;
      return <Text style={styles.fieldValueText}>{truncateText(_value, maxLength)}</Text>;
    };
  };

  // 百分比
  const createPercentageRenderer = () => {
    return (value: any) => {
      if (!value) return null;
      return <Text style={styles.percentageText}>{value}%</Text>;
    };
  };

  // 国籍
  const createNationalityRenderer = () => {
    return (value: any) => {
      return getNationalityDisplay(value);
    };
  };

  // 日期
  const createDateRenderer = (dateFormat?: string) => {
    return (value: any) => {
      if (!value) return null;
      const format = dateFormat || "YYYY-MM-DD";
      try {
        const formattedDate = dayjs(value).format(format);
        return <Text style={styles.fieldValueText}>{formattedDate}</Text>;
      } catch (error) {
        return <Text style={styles.fieldValueText}>{value}</Text>;
      }
    };
  };

  // 金额
  const createAmountRenderer = () => {
    return (value: any) => {
      if (!value) return null;
      // 格式化金额，添加千分位分隔符和 $ 符号
      const numValue = parseFloat(value);
      if (isNaN(numValue)) {
        return <Text style={styles.fieldValueText}>{value}</Text>;
      }
      const formattedAmount = numValue.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      return <Text style={styles.fieldValueText}>$ {formattedAmount}</Text>;
    };
  };

  return {
    createTextRenderer,
    createPercentageRenderer,
    createNationalityRenderer,
    createDateRenderer,
    createAmountRenderer,
  };
};

export type RenderType = "text" | "percentage" | "nationality" | "date" | "amount";

// 快速创建渲染器的工具函数
export const createQuickRenderer = (type: RenderType, options?: any) => {
  const utils = createRenderUtils();

  switch (type) {
    case "text":
      return utils.createTextRenderer(options?.maxLength, options?.defaultValue);
    case "percentage":
      return utils.createPercentageRenderer();
    case "nationality":
      return utils.createNationalityRenderer();
    case "date":
      return utils.createDateRenderer(options?.dateFormat);
    case "amount":
      return utils.createAmountRenderer();
    default:
      return utils.createTextRenderer();
  }
};
